import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CampaignApiRecord,
  CampaignMutationResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { CAMPAIGNS_LIST_QUERY_KEY } from "./campaignQueryCache";
import { unwrapGamePassEntity } from "./responseMappers";

const buildCloneCampaignUrl = (campaignId: string): string =>
  API_ROUTES.CLONE_CAMPAIGN.replace("{campaign_id}", campaignId);

const postCloneCampaign = async (
  campaignId: string,
): Promise<CampaignApiRecord> => {
  const response = await getPrivateInstance().post<CampaignMutationResponse>(
    buildCloneCampaignUrl(campaignId),
    {},
  );
  return unwrapGamePassEntity(response.data, "campaign");
};

/**
 * Clone an existing Monthly Challenge campaign into a new draft via
 * `POST /gamepass/v2/admin/campaigns/{campaign_id}/clone`.
 *
 * The clone always lands as a draft, so rather than patch a single cached
 * record (as schedule / activate / cancel do) we invalidate the campaign lists
 * and let them refetch — the new copy then surfaces in the Drafts column.
 * Errors are propagated so the caller can surface a failure toast. Mirrors
 * `useCloneSeason`.
 */
export const useCloneCampaign = (): UseMutationResult<
  CampaignApiRecord,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "campaigns", "clone"],
    mutationFn: postCloneCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_LIST_QUERY_KEY });
    },
  });
};
